import React, { useEffect, useRef, useState } from "react";
import { Sparkles, Eye, RefreshCw, ChevronDown, ChevronUp, Undo2, AlertTriangle, Lightbulb, MessageSquare, Pencil, History, Scale, Calculator, Repeat, Target, CalendarDays, Trash2, SpellCheck } from "lucide-react";
import { apiFetch } from "../lib/api.js";
import { getCurrentUser } from "../lib/auth.js";
import { spocitejPrijemJs, prijemNaFormular, kcalZMaker, cilFaktorText } from "../lib/prijem.js";
import ConfirmDialog from "./ConfirmDialog.jsx";
import AIGenerating, { odhadPct } from "./AIGenerating.jsx";
import { obsahujeSurovinu } from "../lib/surovinaShoda.js";
import RostouciPole from "./RostouciPole.jsx";

// Jídelníček na míru (10.7.2026, Vylepšení portálu 4.0).
//
// Tok: klientka vyplní dotazník v portálu → tady tým klikne "Vygenerovat
// AI návrh" (Claude API + referenční jídelníčky FitWay40, běží na pozadí
// ~1 min) → návrh zkontroluje/upraví → Zveřejnit → klientka ho vidí
// v portálu (záložka Jídelníček). Data: /api/jidelnicek.
//
// KONCEPT (6.8.2026, Láďa): každá změna v editoru se okamžitě ukládá jako
// koncept do localStorage prohlížeče. Koncept NENÍ uložená verze — klientka ho
// nevidí. Dokud se neuloží, svítí nad editorem žlutý pruh „Neuložený koncept"
// s volbou Zahodit / Uložit a u varianty v seznamu je odznak „koncept".
// Po zavření editoru i po refreshi se koncept nabídne zpět.

// Klíč konceptu v localStorage — jeden na variantu.
const KONCEPT_KEY = (vid) => `fw40-jidelnicek-koncept-${vid}`;

function notionIdFromUrlJs(u) {
  const m = String(u || "").match(/([0-9a-f]{32}|[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})/i);
  return m ? m[1] : "";
}

// Zrcadlí výživový Google Form (jidelnicekLib.DOTAZNIK_MAP).
const DOTAZNIK_LABELS = {
  pohlavi: "Pohlaví", vek: "Věk", vyska: "Výška (cm)", vaha: "Váha (kg)",
  cil: "Cíl", zivotni_styl: "Životní styl",
  alergie: "Alergie", alergie_jine: "Alergie (jiné)",
  vyloucit: "Nechce v jídelníčku", vyloucit_jine: "Nechce (jiné)",
  navyky: "Návyky", tekutiny: "Tekutiny (l/den)",
  pocet_jidel: "Jídel denně", mimo_domov: "Mimo domov (jídel/den)", spanek: "Spánek (h)",
  zmena_vahy: "Změna váhy za rok", alkohol: "Alkohol", oblibena: "Oblíbená jídla",
  pracovni_rezim: "Pracovní režim", zdravotni: "Zdravotní omezení",
  soucasne_stravovani: "Současné stravování", leky: "Léky", poznamka: "Poznámka",
  poznamka_nutricni: "Poznámka nutriční terapeutky (závazná)",
};

// Skupiny pro přehledný detail dotazníku (dropdown, 10.7. v4).
const DOTAZNIK_SKUPINY = [
  { titul: "Základní údaje", klice: ["pohlavi", "vek", "vyska", "vaha", "cil", "zivotni_styl"] },
  { titul: "Omezení a zdraví", klice: ["alergie", "alergie_jine", "vyloucit", "vyloucit_jine", "zdravotni", "leky"], omezeni: true },
  { titul: "Režim a návyky", klice: ["pocet_jidel", "mimo_domov", "tekutiny", "navyky", "alkohol", "zmena_vahy", "spanek", "pracovni_rezim"] },
  { titul: "Vlastními slovy", klice: ["oblibena", "soucasne_stravovani", "poznamka"], dlouhe: true },
  // Poznámka nutriční terapeutky (6.8.2026) — generátor z ní parsuje zákazy
  // a bere ji jako nadřazenou zbytku dotazníku (viz _lib/zakazyLib.cjs).
  { titul: "Pokyny nutriční terapeutky (generátor je musí dodržet)", klice: ["poznamka_nutricni"], dlouhe: true },
];

export default function JidelnicekSection({ klientId, klientNotion, klientName, readOnly = false, onCount }) {
  const id = klientId || notionIdFromUrlJs(klientNotion);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [draft, setDraft] = useState(null); // editovaná kopie varianty (data JSONB)
  const [editId, setEditId] = useState(null); // id varianty otevřené v editoru
  const [origJson, setOrigJson] = useState(null); // JSON uložené verze (pro porovnání + Zahodit)
  const [koncepty, setKoncepty] = useState({});   // { [variantaId]: true } – odznak v seznamu
  const [konceptObnoven, setKonceptObnoven] = useState(false); // koncept se načetl z minula
  const [confirmState, setConfirmState] = useState(null);
  const [planId, setPlanId] = useState(null);   // id varianty s otevřeným panelem "Zveřejnit později"
  const [planKdy, setPlanKdy] = useState("");    // datetime-local hodnota
  const [starsiOpen, setStarsiOpen] = useState(false); // dropdown "Starší návrhy a archiv" (defaultně sbalený)
  const [dotOpen, setDotOpen] = useState(false); // dropdown s detailem dotazníku
  const [dotEdit, setDotEdit] = useState(false);  // úprava dotazníku nutriční (14.7.2026)
  const [dotAns, setDotAns] = useState({});       // editované odpovědi (stringy)
  const [dotHistShow, setDotHistShow] = useState(null); // id rozbalené verze historie
  const [swapBusy, setSwapBusy] = useState(null); // klíč varianty, pro kterou AI vybírá
  const [swapOpen, setSwapOpen] = useState(null); // klíč varianty s otevřeným panelem výměny
  const [swapPozadavek, setSwapPozadavek] = useState("");
  const [swapOptions, setSwapOptions] = useState(null); // nabídnuté náhrady z AI
  // Hromadná záměna suroviny v celém jídelníčku (7.8.2026) — viz panel v editoru.
  const [surZ, setSurZ] = useState("");            // surovina, která se mění
  const [surNa, setSurNa] = useState("");          // čím se nahradí
  const [surBusy, setSurBusy] = useState(false);   // běží AI přepočet
  const [surNalez, setSurNalez] = useState(null);  // { z, na, jidla: [...] } — potvrzovací krok před voláním AI
  const [surZprava, setSurZprava] = useState("");  // hláška pro nutriční („Najdeno v N jídlech")
  const [doplnBusy, setDoplnBusy] = useState(false);   // doplňování chybějících variant
  const [doplnZprava, setDoplnZprava] = useState("");
  const [makraBusy, setMakraBusy] = useState(null); // id receptu klientky s běžícím dopočtem
  // Korektura češtiny nad uloženou variantou (7.8.2026): id varianty, nad
  // kterou právě běží, a výsledek poslední korektury { id, text }.
  const [korBusy, setKorBusy] = useState(null);
  const [korVysledek, setKorVysledek] = useState(null);
  const pollRef = useRef(null);
  // ↶ Zpět v editoru (15.7.2026, zpětná vazba nutritionist): před každou
  // změnou draftu se uloží snapshot. Psaní v jednom poli = JEDEN krok
  // (koalescence podle klíče pole) — šipka vrátí celou úpravu pole najednou.
  const undoStack = useRef([]);   // JSON snapshoty draftu, max 30
  const undoKlic = useRef(null);  // klíč naposledy editovaného pole
  const [undoLen, setUndoLen] = useState(0);
  // Schválení příjmu před generováním (15.7.2026, stejný krok jako u PDF):
  // {pr: {kalorie, bilkoviny, sacharidy, tuky}, vypocet} | null
  const [genPanel, setGenPanel] = useState(null);

  const user = getCurrentUser();
  const role = user?.role || "";
  const canManage = !readOnly && ["owner", "manager", "csm", "nutritionist"].includes(role);
  const isOwner = !readOnly && role === "owner";

  const load = async () => {
    if (!id) { setLoading(false); return; }
    try {
      const r = await apiFetch(`/api/jidelnicek?klient_id=${encodeURIComponent(id)}`);
      const j = await r.json();
      if (j.ok) {
        setData(j);
        if (onCount) onCount(j.zverejneny ? "zveřejněný" : j.navrh ? "návrh" : j.generuje ? "generuje se" : j.dotaznik ? "dotazník vyplněn" : null);
      }
    } catch (_) { /* ticho */ }
    setLoading(false);
  };
  useEffect(() => { load(); }, [id]);

  // Deep-link z nutri dashboardu (?focus=jid-sekce|jid-zadosti|jid-recepty):
  // po načtení sroluje přímo na část k řešení a na 2,5 s ji zvýrazní.
  const focusDone = useRef(false);
  useEffect(() => {
    if (loading || focusDone.current) return;
    let focus = "";
    try { focus = new URLSearchParams(window.location.search).get("focus") || ""; } catch { /* ignore */ }
    if (!["jid-sekce", "jid-zadosti", "jid-recepty"].includes(focus)) return;
    focusDone.current = true;
    setTimeout(() => {
      const el = document.getElementById(focus) || document.getElementById("jid-sekce");
      if (!el) return;
      el.scrollIntoView({ behavior: "smooth", block: "start" });
      el.classList.add("ring-2", "ring-brand-400", "rounded-xl");
      setTimeout(() => el.classList.remove("ring-2", "ring-brand-400"), 2500);
    }, 350);
  }, [loading]);

  // Polling během generování (background funkce běží ~1 min).
  const genStartRef = React.useRef(null);
  useEffect(() => {
    if (data?.generuje && !genStartRef.current) genStartRef.current = Date.now();
    if (!data?.generuje) genStartRef.current = null;
    if (data?.generuje) {
      pollRef.current = setInterval(load, 5000);
      return () => clearInterval(pollRef.current);
    }
  }, [data?.generuje]);

  const post = async (payload) => {
    setErr("");
    try {
      const r = await apiFetch("/api/jidelnicek", { method: "POST", body: JSON.stringify(payload) });
      const j = await r.json();
      if (!j.ok) { setErr(j.error || "Akce selhala"); return false; }
      return true;
    } catch (e) { setErr(e.message || "Akce selhala"); return false; }
  };

  // Jako post(), ale vrací celý JSON (pro akce s návratovou hodnotou).
  const postData = async (payload) => {
    setErr("");
    try {
      const r = await apiFetch("/api/jidelnicek", { method: "POST", body: JSON.stringify(payload) });
      const j = await r.json();
      if (!j.ok) { setErr(j.error || "Akce selhala"); return null; }
      return j;
    } catch (e) { setErr(e.message || "Akce selhala"); return null; }
  };

  // Klik na Vygenerovat NEgeneruje rovnou — nejdřív se schválí kcal a makra
  // (stejný krok jako u PDF jídelníčku, 15.7.2026).
  const generate = () => {
    setDraft(null); setEditId(null);
    const v = spocitejPrijemJs(data?.dotaznik?.data || {});
    setGenPanel({ pr: prijemNaFormular(v), vypocet: v });
  };
  const genZmenMakro = (k, val) => setGenPanel((p) => {
    const pr = { ...p.pr, [k]: val };
    pr.kalorie = String(kcalZMaker(pr) || "");
    return { ...p, pr };
  });
  // Kcal se přepisuje volně, makra se doškálují až při opuštění pole.
  const genZmenKcalBlur = () => setGenPanel((p) => {
    const cilK = parseInt(p.pr.kalorie, 10) || 0;
    const cur = kcalZMaker(p.pr);
    if (!cilK || !cur) return p;
    const f = cilK / cur;
    const n = (x) => String(Math.round((parseInt(p.pr[x], 10) || 0) * f));
    const pr = { kalorie: p.pr.kalorie, bilkoviny: n("bilkoviny"), sacharidy: n("sacharidy"), tuky: n("tuky") };
    pr.kalorie = String(kcalZMaker(pr) || "");
    return { ...p, pr };
  });
  const spustGenerovani = async () => {
    if (!genPanel) return;
    const n = (x) => parseInt(String(genPanel.pr[x] || "").trim(), 10) || 0;
    const b = n("bilkoviny"), s = n("sacharidy"), t = n("tuky");
    const v = genPanel.vypocet;
    const payload = { action: "generate", klient_id: id };
    // Cíl se posílá jen když se liší od výpočtu (jinak zůstane "z výpočtu").
    const rucni = b && s && t && !(v && b === v.makra.bilkoviny && s === v.makra.sacharidy && t === v.makra.tuky);
    if (rucni) payload.cil = { kalorie: kcalZMaker(genPanel.pr), makra: { bilkoviny: b, sacharidy: s, tuky: t } };
    setGenPanel(null);
    if (await post(payload)) await load();
  };

  const saveDraft = async () => {
    if (!editId || !draft) return;
    setSaving(true);
    if (await post({ action: "save", id: editId, data: draft })) {
      // Uložením se koncept stává uloženou verzí → pruh i odznak zmizí.
      setOrigJson(JSON.stringify(draft));
      setKonceptObnoven(false);
      smazKoncept(editId);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
      await load();
    }
    setSaving(false);
  };

  const publish = (variant) => {
    if (!variant) return;
    setConfirmState({
      title: "Zveřejnit tuto variantu klientce?",
      description: <>Varianta se okamžitě zobrazí <strong>{klientName}</strong> v portálu. Dosud zveřejněná varianta se přesune do archivu (zůstane v seznamu).</>,
      confirmLabel: "Zveřejnit",
      variant: "primary",
      onConfirm: async () => {
        if (editId === variant.id && draft) {
          await post({ action: "save", id: variant.id, data: draft });
          setOrigJson(JSON.stringify(draft)); setKonceptObnoven(false);
        }
        smazKoncept(variant.id);
        if (await post({ action: "publish", id: variant.id })) await load();
      },
    });
  };

  // Plánované zveřejnění (14.7.2026): návrh → naplánováno, zveřejní cron
  // (každých 10 min). Hodnota pro <input type="datetime-local">.
  const presetKdy = (dny, hodina) => {
    const d = new Date();
    d.setDate(d.getDate() + dny);
    d.setHours(hodina, 0, 0, 0);
    const p = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
  };
  const fmtKdy = (d) => {
    try {
      const x = new Date(d);
      return `${x.toLocaleDateString("cs-CZ")} ${String(x.getHours()).padStart(2, "0")}:${String(x.getMinutes()).padStart(2, "0")}`;
    } catch { return ""; }
  };
  const zverejnitPozdeji = async (variant) => {
    if (!planKdy) return;
    if (editId === variant.id && draft) {
      await post({ action: "save", id: variant.id, data: draft });
      setOrigJson(JSON.stringify(draft)); setKonceptObnoven(false);
    }
    smazKoncept(variant.id);
    if (await post({ action: "zverejnit-pozdeji", id: variant.id, kdy: new Date(planKdy).toISOString() })) {
      setPlanId(null); setPlanKdy("");
      await load();
    }
  };
  const zrusitPlanZverejneni = async (variant) => {
    if (await post({ action: "zverejnit-pozdeji-zrusit", id: variant.id })) await load();
  };

  const deleteVariant = (variant) => {
    setConfirmState({
      title: "Smazat tuto variantu?",
      description: <>Varianta z {new Date(variant.created_at).toLocaleDateString("cs-CZ")} se nenávratně smaže.</>,
      confirmLabel: "Smazat",
      variant: "danger",
      onConfirm: async () => {
        if (editId === variant.id) { setDraft(null); setEditId(null); setOrigJson(null); }
        smazKoncept(variant.id);
        if (await post({ action: "smazat-variantu", id: variant.id, klient_id: id })) await load();
      },
    });
  };

  const archiveVariant = (variant) => {
    setConfirmState({
      title: "Archivovat tuto variantu?",
      description: <>Varianta se přesune do archivu, zůstane v seznamu, ale bude označená jako neaktivní.</>,
      confirmLabel: "Archivovat",
      variant: "warning",
      onConfirm: async () => {
        if (editId === variant.id) { setDraft(null); setEditId(null); setOrigJson(null); }
        smazKoncept(variant.id);
        if (await post({ action: "archivovat-variantu", id: variant.id, klient_id: id })) await load();
      },
    });
  };

  // ── Korektura češtiny nad ULOŽENOU variantou (7.8.2026) ──────────────────
  // Korektor jinak běží jen při generování, takže starší jídelníčky si své
  // chyby („cukankového", „opečete") nesou v DB napořád. Tohle ho pustí znovu
  // a server výsledek rovnou uloží — proto po doběhnutí načítáme seznam.

  // České skloňování počtu (1 výraz / 2 výrazy / 5 výrazů).
  const pocetSlovem = (n, [jeden, dva, pet]) => `${n} ${n === 1 ? jeden : n >= 2 && n <= 4 ? dva : pet}`;

  // Statistika z korektoru → věta pro nutriční.
  const korekturaZprava = (stat) => {
    const s = stat || {};
    const opraveno = (s.slovnik || 0) + (s.ai || 0);
    const casti = [];
    if (opraveno) casti.push(`Opraveno ${pocetSlovem(opraveno, ["výraz", "výrazy", "výrazů"])}`);
    if (s.sloh) casti.push(pocetSlovem(s.sloh, ["slohová úprava", "slohové úpravy", "slohových úprav"]));
    if (!casti.length) return `Čeština je v pořádku, nic k opravě.${s.chyba ? ` (AI korektura selhala: ${s.chyba})` : ""}`;
    let txt = `${casti.join(", ")}.`;
    if (s.zbyva) txt += ` Podezřelých textů zůstává ${s.zbyva}, projděte je ručně v editoru.`;
    if (s.chyba) txt += ` AI korektura selhala: ${s.chyba}`;
    return txt;
  };

  const spustKorekturu = async (variant) => {
    setKorVysledek(null);
    setKorBusy(variant.id);
    const j = await postData({ action: "korektura", id: variant.id });
    setKorBusy(null);
    if (!j) return; // chybu už zobrazil postData přes setErr
    let text = korekturaZprava(j.stat);
    // Rozdělaný koncept drží starou verzi textů — kdyby ho nutriční uložila,
    // korekturu by přepsala. Radši na to upozorníme hned.
    if (koncepty[variant.id]) {
      text += " Máte u varianty rozdělaný koncept, než ho uložíte, otevřete variantu znovu, ať korekturu nepřepíšete.";
    }
    setKorVysledek({ id: variant.id, text });
    await load();
  };

  const deleteDotaznik = () => {
    setConfirmState({
      title: "Smazat vyplněný dotazník?",
      description: <>Dotazník <strong>{klientName}</strong> se nenávratně smaže, klientka ho v portálu vyplní znovu. Vygenerované varianty jídelníčku zůstávají.</>,
      confirmLabel: "Smazat dotazník",
      variant: "danger",
      onConfirm: async () => {
        if (await post({ action: "smazat-dotaznik", klient_id: id })) await load();
      },
    });
  };

  // ——— Koncept (neuložené změny) ———————————————————————————————
  const smazKoncept = (vid) => {
    try { localStorage.removeItem(KONCEPT_KEY(vid)); } catch { /* private mode */ }
    setKoncepty((k) => { if (!k[vid]) return k; const n = { ...k }; delete n[vid]; return n; });
  };

  // Rozdíl proti uložené verzi = máme neuložený koncept.
  const dirty = !!(editId && draft && origJson !== null && JSON.stringify(draft) !== origJson);

  // Každá změna draftu se hned zapíše (nebo smaže) v localStorage.
  useEffect(() => {
    if (!editId || !draft || origJson === null) return;
    const cur = JSON.stringify(draft);
    if (cur === origJson) { smazKoncept(editId); return; }
    try {
      localStorage.setItem(KONCEPT_KEY(editId), JSON.stringify({ ulozeno: new Date().toISOString(), data: draft }));
    } catch { /* plná kvóta / private mode – koncept prostě nebude */ }
    setKoncepty((k) => (k[editId] ? k : { ...k, [editId]: true }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft, editId, origJson]);

  // Odznaky „koncept" v seznamu variant (po každém načtení dat).
  useEffect(() => {
    const m = {};
    for (const v of data?.varianty || []) {
      try { if (localStorage.getItem(KONCEPT_KEY(v.id))) m[v.id] = true; } catch { /* ignore */ }
    }
    setKoncepty(m);
  }, [data?.varianty]);

  // Pojistka proti zavření záložky s rozdělanou úpravou.
  useEffect(() => {
    if (!dirty) return;
    const h = (e) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", h);
    return () => window.removeEventListener("beforeunload", h);
  }, [dirty]);

  const zahodKoncept = () => {
    if (!editId || origJson === null) return;
    setConfirmState({
      title: "Zahodit neuložené změny?",
      description: <>Editor se vrátí k poslední <strong>uložené</strong> verzi varianty a rozpracovaný koncept se smaže. Tohle nejde vzít zpět.</>,
      confirmLabel: "Zahodit změny",
      variant: "danger",
      onConfirm: () => {
        const vid = editId;
        setDraft(JSON.parse(origJson));
        undoStack.current = []; undoKlic.current = null; setUndoLen(0);
        setKonceptObnoven(false);
        smazKoncept(vid);
      },
    });
  };

  const zavritEditor = () => {
    const zavri = () => { setEditId(null); setDraft(null); setOrigJson(null); setKonceptObnoven(false); };
    if (!dirty) { zavri(); return; }
    setConfirmState({
      title: "Zavřít editor s neuloženými změnami?",
      description: <>Koncept zůstane uložený v tomhle prohlížeči a při dalším otevření varianty ho nabídneme zpět. Klientka ho ale <strong>neuvidí</strong>, dokud nedáš Uložit úpravy.</>,
      confirmLabel: "Zavřít a nechat koncept",
      cancelLabel: "Zpět do editoru",
      variant: "warning",
      onConfirm: zavri,
    });
  };

  const openEditor = (variant) => {
    const orig = JSON.stringify(variant.data);
    let start = JSON.parse(orig);
    let obnoveno = false;
    try {
      const raw = localStorage.getItem(KONCEPT_KEY(variant.id));
      const k = raw ? JSON.parse(raw) : null;
      if (k && k.data && JSON.stringify(k.data) !== orig) { start = k.data; obnoveno = true; }
    } catch { /* poškozený koncept ignorujeme */ }
    setEditId(variant.id);
    setOrigJson(orig);
    setDraft(start);
    setKonceptObnoven(obnoveno);
    undoStack.current = []; undoKlic.current = null; setUndoLen(0);
  };

  // Souhrn varianty do řádku: kcal, makra v gramech, počet chodů (a dní)
  const variantSummary = (v) => {
    const d = v.data || {};
    const m = d.makra || {};
    const chodyCount = Array.isArray(d.dny) && d.dny.length
      ? `${(d.dny[0]?.chody || []).length} chodů × ${d.dny.length} dní`
      : `${(d.chody || []).length} chodů`;
    return { kcal: d.kalorie || "—", b: m.bilkoviny ?? "—", s: m.sacharidy ?? "—", t: m.tuky ?? "—", chodyCount };
  };

  const unpublish = () => {
    if (!data?.zverejneny) return;
    setConfirmState({
      title: "Stáhnout jídelníček z portálu?",
      description: <>Klientka místo něj uvidí „připravujeme". Jídelníček zůstane jako návrh k úpravám.</>,
      confirmLabel: "Stáhnout",
      variant: "danger",
      onConfirm: async () => {
        if (await post({ action: "unpublish", id: data.zverejneny.id })) await load();
      },
    });
  };

  if (!id) return null;
  if (loading) return <div className="text-sm text-slate-400 py-2">Načítám jídelníček…</div>;

  const dotaznik = data?.dotaznik;
  // Klíče, které portál ukládá jako pole (chips multi-select).
  const DOT_POLE = ["alergie", "vyloucit", "navyky"];
  const zacniUpravuDotazniku = () => {
    const d = dotaznik?.data || {};
    const init = {};
    Object.keys(DOTAZNIK_LABELS).forEach((k) => {
      const v = d[k];
      init[k] = Array.isArray(v) ? v.join(", ") : (v == null ? "" : String(v));
    });
    setDotAns(init);
    setDotEdit(true);
  };
  const ulozUpravuDotazniku = async () => {
    const d = dotaznik?.data || {};
    const answers = {};
    Object.keys(DOTAZNIK_LABELS).forEach((k) => {
      const txt = String(dotAns[k] == null ? "" : dotAns[k]).trim();
      if (!txt) return;
      answers[k] = (Array.isArray(d[k]) || DOT_POLE.includes(k))
        ? txt.split(",").map((x) => x.trim()).filter(Boolean)
        : txt;
    });
    setSaving(true);
    const ok = await post({ action: "dotaznik-uprava", klient_id: id, answers });
    setSaving(false);
    if (ok) { setDotEdit(false); await load(); }
  };
  const navrh = data?.navrh;
  const zverejneny = data?.zverejneny;

  // Editor helpers — mutace draftu (immutable copy).
  const zapamatujProUndo = (klic) => {
    if (!draft) return;
    if (klic && undoKlic.current === klic) return; // pokračující psaní v témže poli
    undoKlic.current = klic || null;
    const st = undoStack.current;
    st.push(JSON.stringify(draft));
    if (st.length > 30) st.shift();
    setUndoLen(st.length);
  };
  const zpetDraft = () => {
    const st = undoStack.current;
    if (!st.length) return;
    undoKlic.current = null;
    setDraft(JSON.parse(st.pop()));
    setUndoLen(st.length);
  };
  const setDraftField = (path, value) => {
    zapamatujProUndo(path.join("."));
    setDraft((d) => {
      const n = JSON.parse(JSON.stringify(d || {}));
      let o = n;
      for (let i = 0; i < path.length - 1; i++) o = o[path[i]];
      o[path[path.length - 1]] = value;
      return n;
    });
  };

  // ⚖️ Deterministický přepočet porcí (13.7.): přeškáluje kcal/makra všech
  // receptů a gramáže (g/ml) v surovinách faktorem cílové kcal / současný
  // průměr denních součtů. Kusová množství (ks, plátky) nechává — ruční check.
  const prepocitatPorce = () => {
    zapamatujProUndo();
    setDraft((d) => {
      const nDraft = JSON.parse(JSON.stringify(d || {}));
      const target = Number(nDraft.kalorie) || 0;
      const dnyArr = Array.isArray(nDraft.dny) && nDraft.dny.length
        ? nDraft.dny.map((x) => x.chody || [])
        : [nDraft.chody || []];
      const denniSoucty = [];
      for (const chody of dnyArr) {
        let s = 0;
        for (const ch of chody) {
          const vs = (ch.varianty || []).map((v) => Number(v.kcal) || 0).filter(Boolean);
          if (vs.length) s += vs.reduce((a, b) => a + b, 0) / vs.length;
        }
        if (s) denniSoucty.push(s);
      }
      const prum = denniSoucty.length ? denniSoucty.reduce((a, b) => a + b, 0) / denniSoucty.length : 0;
      if (!target || !prum) { setErr("Přepočet: chybí cílové kcal nebo kcal u receptů."); return d; }
      const f = target / prum;
      if (Math.abs(f - 1) < 0.005) return d;
      const scaleNum = (x) => { const v = x * f; return v >= 20 ? Math.round(v / 5) * 5 : Math.max(1, Math.round(v)); };
      const scaleText = (s) => String(s).replace(/(\d+(?:[.,]\d+)?)(\s*(?:g|ml)\b)/gi,
        (_, num, unit) => String(scaleNum(parseFloat(String(num).replace(",", ".")))) + unit);
      for (const chody of dnyArr) for (const ch of chody) for (const v of ch.varianty || []) {
        for (const fld of ["kcal", "b", "s", "t"]) if (v[fld] != null) v[fld] = Math.round((Number(v[fld]) || 0) * f);
        if (Array.isArray(v.suroviny)) v.suroviny = v.suroviny.map(scaleText);
        if (v.popis) v.popis = scaleText(v.popis); // starší tvar má gramáže v popisu
      }
      // Makra hlavičky se NEškálují — jsou cílové (dopočítané ze zadaných kcal).
      return nDraft;
    });
  };

  // ✨ Výměna receptu (13.7.): panel u receptu — AI nabídne 3 náhrady z báze
  // (stejný chod, bez alergenů klientky, porce na kcal původního receptu),
  // uživatel vybere kliknutím. Jen v draftu — uloží se přes "Uložit úpravy".
  const nabidniRecepty = async (ch, v, key) => {
    setSwapBusy(key);
    setSwapOptions(null);
    const j = await postData({
      action: "nahradit-recept", klient_id: id, chod: ch.nazev || "",
      cilKcal: Number(v.kcal) || 400,
      vynechat: (ch.varianty || []).map((x) => x.nazev).filter(Boolean),
      pozadavek: swapPozadavek || "",
    });
    setSwapBusy(null);
    if (Array.isArray(j?.recepty) && j.recepty.length) setSwapOptions(j.recepty);
  };

  // 21.9.2026: doplnění chybějících variant do HOTOVÉHO jídelníčku. Chod
  // s méně než 5 variantami dostane další recepty z databáze (stejná akce
  // jako Vyměnit recept: kcal chodu, zákazy, bez opakování v celém týdnu).
  // Hotové varianty zůstávají beze změny, jde to jen do konceptu.
  const VARIANT_NA_CHOD = 5;
  const chybejiciVarianty = (jd) => {
    const bloky = Array.isArray(jd?.dny) && jd.dny.length
      ? jd.dny.map((den, di) => ({ chody: den.chody || [], basePath: ["dny", di, "chody"] }))
      : [{ chody: jd?.chody || [], basePath: ["chody"] }];
    const out = [];
    for (const { chody, basePath } of bloky) {
      (chody || []).forEach((ch, ci) => {
        const n = (ch.varianty || []).length;
        if (n > 0 && n < VARIANT_NA_CHOD) out.push({ basePath, ci, chod: ch.nazev || "", chybi: VARIANT_NA_CHOD - n });
      });
    }
    return out;
  };
  const doplnVarianty = async () => {
    if (!draft) return;
    const chybi = chybejiciVarianty(draft);
    if (!chybi.length) return;
    setDoplnBusy(true); setDoplnZprava("");
    const pracovni = JSON.parse(JSON.stringify(draft));
    const vsechnaJmena = new Set();
    const bloky = Array.isArray(pracovni.dny) && pracovni.dny.length ? pracovni.dny.map((d) => d.chody || []) : [pracovni.chody || []];
    for (const chody of bloky) for (const ch of chody) for (const v of ch.varianty || []) if (v.nazev) vsechnaJmena.add(v.nazev);
    let doplneno = 0;
    let nedoplneno = 0;
    for (const m of chybi) {
      let o = pracovni;
      for (const p of m.basePath) o = o[p];
      const ch = o[m.ci];
      const kcals = (ch.varianty || []).map((v) => Number(v.kcal) || 0).filter(Boolean);
      const cilKcal = kcals.length ? Math.round(kcals.reduce((a, b) => a + b, 0) / kcals.length) : 400;
      for (let pokus = 0; pokus < 4 && ch.varianty.length < VARIANT_NA_CHOD; pokus++) {
        const j = await postData({ action: "nahradit-recept", klient_id: id, chod: ch.nazev || "", cilKcal, vynechat: [...vsechnaJmena] });
        const recepty = Array.isArray(j?.recepty) ? j.recepty : [];
        if (!recepty.length) break;
        for (const r of recepty) {
          if (ch.varianty.length >= VARIANT_NA_CHOD) break;
          if (vsechnaJmena.has(r.nazev)) continue;
          ch.varianty.push({ nazev: r.nazev, kcal: r.kcal, b: r.b, s: r.s, t: r.t, suroviny: r.suroviny, postup: r.postup });
          vsechnaJmena.add(r.nazev);
          doplneno++;
        }
      }
      if (ch.varianty.length < VARIANT_NA_CHOD) nedoplneno += VARIANT_NA_CHOD - ch.varianty.length;
    }
    zapamatujProUndo();
    setDraft(pracovni);
    setDoplnBusy(false);
    setDoplnZprava(doplneno
      ? `Doplněno ${doplneno} ${doplneno === 1 ? "variantu" : doplneno < 5 ? "varianty" : "variant"}${nedoplneno ? `, ${nedoplneno} se doplnit nepodařilo` : ""}. Zkontrolujte je a uložte tlačítkem „Uložit úpravy".`
      : "Žádný vhodný recept k doplnění se nenašel.");
    if (doplneno) setErr("");
  };

  const pouzitRecept = (basePath, ci, vi, r) => {
    zapamatujProUndo();
    setDraft((d) => {
      const nDraft = JSON.parse(JSON.stringify(d));
      let o = nDraft;
      for (const p of basePath) o = o[p];
      const vars = o[ci].varianty;
      vars[vi] = { ...vars[vi], nazev: r.nazev, kcal: r.kcal, b: r.b, s: r.s, t: r.t, suroviny: r.suroviny, postup: r.postup };
      delete vars[vi].popis;
      return nDraft;
    });
    setSwapOpen(null); setSwapOptions(null); setSwapPozadavek("");
  };

  // 🔁 Hromadná záměna suroviny (7.8.2026): nutriční vymění jednu surovinu
  // napříč celým jídelníčkem (např. rostlinný → syrovátkový protein) bez
  // přegenerování celé varianty — AI dostane jen zasažená jídla.

  // Porovnání bez diakritiky a velikosti písmen — nutriční surovinu píše volně.
  const normSur = (s) => String(s || "").toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "");

  // Projde draft (denní i týdenní tvar) a vrátí zasažená jídla i s cestou,
  // kam se pak výsledek z AI dosadí zpátky.
  const najdiJidlaSeSurovinou = (jd, hledane) => {
    const q = normSur(hledane).trim();
    const nalezena = [];
    if (!q) return nalezena;
    const bloky = Array.isArray(jd?.dny) && jd.dny.length
      ? jd.dny.map((den, di) => ({ chody: den.chody || [], basePath: ["dny", di, "chody"], den: den.den || `Den ${di + 1}` }))
      : [{ chody: jd?.chody || [], basePath: ["chody"], den: "" }];
    for (const { chody, basePath, den } of bloky) {
      (chody || []).forEach((ch, ci) => {
        (ch.varianty || []).forEach((v, vi) => {
          const suroviny = Array.isArray(v.suroviny) ? v.suroviny : [];
          if (!suroviny.some((s) => obsahujeSurovinu(s, hledane))) return;
          nalezena.push({
            basePath, ci, vi, den, chod: ch.nazev || "",
            nazev: v.nazev || "", suroviny,
            postup: v.postup ?? v.popis ?? "",
            kcal: Number(v.kcal) || 0, b: Number(v.b) || 0, s: Number(v.s) || 0, t: Number(v.t) || 0,
          });
        });
      });
    }
    return nalezena;
  };

  // Krok 1 — spočítat zásah lokálně z draftu. AI se zatím nevolá, nutriční
  // nejdřív uvidí, kolika jídel se to týká (a odchytí překlep).
  const najdiSurovinu = () => {
    setErr("");
    setSurNalez(null);
    const z = surZ.trim();
    const na = surNa.trim();
    if (!z || !na) { setSurZprava("Vyplňte obě pole: kterou surovinu měnit a čím ji nahradit."); return; }
    const nalezena = najdiJidlaSeSurovinou(draft, z);
    if (!nalezena.length) {
      setSurZprava(`Surovinu „${z}" jsme v tomhle jídelníčku nenašli, zkontrolujte překlep. AI jsme nevolali.`);
      return;
    }
    setSurZprava(`Najdeno v ${nalezena.length} ${nalezena.length === 1 ? "jídle" : "jídlech"}.`);
    setSurNalez({ z, na, jidla: nalezena });
  };

  // Krok 2 — potvrzeno: AI přepočítá jen zasažená jídla a výsledek jde do
  // draftu (ne do DB) — uloží se až stávajícím tlačítkem „Uložit úpravy".
  const spustZamenuSuroviny = async () => {
    if (!surNalez || surBusy) return;
    setSurBusy(true);
    const j = await postData({
      action: "zamenit-surovinu", klient_id: id, z: surNalez.z, na: surNalez.na,
      jidla: surNalez.jidla.map((x) => ({
        nazev: x.nazev, suroviny: x.suroviny, postup: x.postup,
        kcal: x.kcal, b: x.b, s: x.s, t: x.t,
      })),
    });
    setSurBusy(false);
    if (!j) return; // chybu už zobrazil postData
    const vysledky = Array.isArray(j.jidla) ? j.jidla : [];
    if (!vysledky.length) { setErr("AI nevrátila žádné upravené jídlo, zkuste to znovu."); return; }
    zapamatujProUndo();
    setDraft((d) => {
      const nDraft = JSON.parse(JSON.stringify(d));
      for (const r of vysledky) {
        const cil = surNalez.jidla[r.index];
        if (!cil) continue;
        let o = nDraft;
        for (const p of cil.basePath) o = o[p];
        const vars = o?.[cil.ci]?.varianty;
        if (!vars || !vars[cil.vi]) continue;
        vars[cil.vi] = {
          ...vars[cil.vi],
          nazev: r.nazev || vars[cil.vi].nazev,
          suroviny: Array.isArray(r.suroviny) && r.suroviny.length ? r.suroviny : vars[cil.vi].suroviny,
          postup: r.postup, kcal: r.kcal, b: r.b, s: r.s, t: r.t,
        };
        delete vars[cil.vi].popis; // gramáže má nově Postup, starý popis by mátl
      }
      return nDraft;
    });
    setSurZprava(`Zaměněno v ${vysledky.length} ${vysledky.length === 1 ? "jídle" : "jídlech"}. Zkontrolujte a uložte tlačítkem „Uložit úpravy", teprve pak to uvidí klientka.`);
    setSurNalez(null);
  };

  // Změna kcal/den → makra se hned dopočítají. Zachovává AKTUÁLNÍ poměr maker
  // (energetické podíly z gramů B×4/S×4/T×9); bez maker výchozí 25/45/30.
  const setKcalSMakry = (kcal) => {
    zapamatujProUndo("kalorie+makra");
    setDraft((d) => {
      const nDraft = JSON.parse(JSON.stringify(d || {}));
      const eB = (Number(nDraft.makra?.bilkoviny) || 0) * 4;
      const eS = (Number(nDraft.makra?.sacharidy) || 0) * 4;
      const eT = (Number(nDraft.makra?.tuky) || 0) * 9;
      const tot = eB + eS + eT;
      const pB = tot > 0 ? eB / tot : 0.25;
      const pS = tot > 0 ? eS / tot : 0.45;
      const pT = tot > 0 ? eT / tot : 0.30;
      nDraft.kalorie = kcal;
      nDraft.makra = {
        ...(nDraft.makra || {}),
        bilkoviny: Math.round(kcal * pB / 4),
        sacharidy: Math.round(kcal * pS / 4),
        tuky: Math.round(kcal * pT / 9),
      };
      return nDraft;
    });
  };

  // Presety poměrů maker [%B, %S, %T] — klik přepočítá gramy ze zadaných kcal.
  const MAKRA_PRESETY = [
    { label: "Vyvážený", pct: [25, 45, 30] },
    { label: "Více bílkovin", pct: [30, 40, 30] },
    { label: "Low-carb", pct: [30, 30, 40] },
    { label: "Vytrvalost", pct: [20, 55, 25] },
  ];
  const aplikujPreset = (pct) => {
    zapamatujProUndo();
    setDraft((d) => {
      const nDraft = JSON.parse(JSON.stringify(d || {}));
      const k = Number(nDraft.kalorie) || 0;
      if (!k) { setErr("Nejdřív zadejte kcal/den."); return d; }
      nDraft.makra = {
        ...(nDraft.makra || {}),
        bilkoviny: Math.round(k * pct[0] / 100 / 4),
        sacharidy: Math.round(k * pct[1] / 100 / 4),
        tuky: Math.round(k * pct[2] / 100 / 9),
      };
      return nDraft;
    });
  };
  const presetAktivni = (jd, pct) => {
    const k = Number(jd.kalorie) || 0;
    if (!k) return false;
    const cil = [Math.round(k * pct[0] / 100 / 4), Math.round(k * pct[1] / 100 / 4), Math.round(k * pct[2] / 100 / 9)];
    const je = [Number(jd.makra?.bilkoviny) || 0, Number(jd.makra?.sacharidy) || 0, Number(jd.makra?.tuky) || 0];
    return cil.every((c, i) => Math.abs(c - je[i]) <= 2);
  };

  // Nový poměr maker se do receptů nepromítne škálováním — jde jen přes AI
  // regeneraci. Vygeneruje NOVOU variantu s ručním cílem, současné zůstávají.
  const regenerovatSCilem = () => {
    if (!draft?.kalorie || !draft?.makra) return;
    setConfirmState({
      title: "Vygenerovat novou variantu s těmito makry?",
      description: <>AI vygeneruje nový týdenní jídelníček na <strong>{draft.kalorie} kcal</strong> (B {draft.makra.bilkoviny} g, S {draft.makra.sacharidy} g, T {draft.makra.tuky} g) a skladbu jídel přizpůsobí tomuto poměru. Běží ~2 minuty, současné varianty zůstávají v seznamu.</>,
      confirmLabel: "Vygenerovat",
      variant: "primary",
      onConfirm: async () => {
        const cil = { kalorie: draft.kalorie, makra: draft.makra };
        if (await post({ action: "generate", klient_id: id, cil })) { setEditId(null); setDraft(null); await load(); }
      },
    });
  };

  // Editor chodů (varianty) — sdílené pro denní (root chody) i týdenní (dny[].chody) tvar.
  // Emoji chodu podle názvu — čistě vizuální rozlišení v accordionu.
  const chodEmoji = (nazev) => {
    const n = String(nazev || "").toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "");
    if (n.includes("snid")) return "🍳";
    if (n.includes("obed")) return "🍽️";
    if (n.includes("vecer")) return "🌙";
    if (n.includes("svac")) return "🍎";
    return "🥗";
  };

  const renderChody = (chody, basePath) => (
    (chody || []).map((ch, ci) => (
      <details key={ci} className="border border-slate-200 rounded-xl bg-white overflow-hidden" open={false}>
        <summary className="flex items-center gap-2 px-3.5 py-2.5 text-sm font-semibold text-slate-800 cursor-pointer bg-slate-50/80 hover:bg-slate-100/80 select-none">
          <span className="text-base leading-none">{chodEmoji(ch.nazev)}</span>
          {ch.nazev || `Chod ${ci + 1}`}
          <span className="text-[11px] font-medium text-slate-500 bg-white border border-slate-200 rounded-full px-2 py-0.5">{(ch.varianty || []).length} variant</span>
        </summary>
        <div className="p-3 space-y-2.5 border-t border-slate-100">
          {(ch.varianty || []).map((v, vi) => {
            const vKey = [...basePath, ci, vi].join(".");
            return (
            <div key={vi} className="border border-slate-200 rounded-xl p-3 bg-white shadow-sm space-y-2">
              {/* Jeden řádek: číslo, název, makra, Nahradit — rychlá kontrola bez skákání očima */}
              <div className="flex flex-wrap gap-2 items-center">
                <span className="w-6 h-6 rounded-full bg-slate-100 text-slate-500 text-[11px] font-bold flex items-center justify-center flex-shrink-0">{vi + 1}</span>
                <input type="text" value={v.nazev || ""} placeholder="Název jídla"
                  onChange={(e) => setDraftField([...basePath, ci, "varianty", vi, "nazev"], e.target.value)}
                  className="field flex-1 min-w-[220px] font-semibold" />
                <div className="inline-flex items-center gap-1 bg-slate-50 border border-slate-200 rounded-xl px-2 py-1">
                  {[["kcal", "kcal", "w-14"], ["b", "B", "w-11"], ["s", "S", "w-11"], ["t", "T", "w-11"]].map(([f, l, w]) => (
                    <label key={f} className="flex items-center gap-1 text-[11px] font-semibold text-slate-400 uppercase">
                      <span>{l}</span>
                      <input type="number" value={v[f] ?? ""}
                        onChange={(e) => setDraftField([...basePath, ci, "varianty", vi, f], parseInt(e.target.value, 10) || 0)}
                        className={`${w} text-xs font-semibold text-slate-700 bg-white border border-slate-200 rounded-lg px-1 py-1 text-center focus:border-plum-300 focus:outline-none`} />
                    </label>
                  ))}
                </div>
                {canManage && (
                  <button type="button" title="AI nabídne 3 náhrady z báze receptů (stejný chod, bez alergenů klientky, stejné kcal)"
                    onClick={() => { setSwapOpen(swapOpen === vKey ? null : vKey); setSwapOptions(null); setSwapPozadavek(""); }}
                    className={`inline-flex items-center gap-1.5 text-xs font-semibold rounded-xl px-3 py-1.5 shadow-sm whitespace-nowrap transition-colors ${
                      swapOpen === vKey
                        ? "text-brand-700 bg-brand-100 border border-brand-300"
                        : "text-white bg-gradient-to-r from-brand-600 to-plum-600 hover:from-brand-700 hover:to-plum-700"
                    }`}>
                    <Sparkles className="w-3.5 h-3.5" /> {swapOpen === vKey ? "Zavřít" : "Nahradit"}
                  </button>
                )}
              </div>

              {canManage && swapOpen === vKey && (
                <div className="bg-gradient-to-br from-brand-50 to-plum-50 border border-brand-200 rounded-xl p-3 space-y-2">
                  <div className="flex gap-2 flex-wrap items-center">
                    <input value={swapPozadavek} onChange={(e) => setSwapPozadavek(e.target.value)}
                      placeholder="Volitelný požadavek, např. bez mléčných výrobků, něco rychlého…"
                      className="field flex-1 min-w-[200px]" />
                    <button type="button" onClick={() => nabidniRecepty(ch, v, vKey)} disabled={swapBusy === vKey}
                      className="btn btn-primary whitespace-nowrap">
                      <Sparkles className="w-3.5 h-3.5" /> {swapBusy === vKey ? "AI vybírá z báze…" : swapOptions ? "Navrhnout jiné 3" : "Navrhnout 3 recepty"}
                    </button>
                  </div>
                  {swapBusy === vKey && (
                    <div className="text-[11px] text-brand-500">Prohledávám bázi receptů a rozepisuji porce na {v.kcal || "?"} kcal, pár sekund…</div>
                  )}
                  {Array.isArray(swapOptions) && swapOptions.map((r, ri) => (
                    <div key={ri} className="flex items-start gap-2.5 bg-white border border-brand-100 hover:border-brand-300 rounded-xl p-2.5">
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-semibold text-slate-800">{r.nazev}</div>
                        <div className="text-[11px] font-medium text-plum-600 mt-0.5">{r.kcal} kcal, B {r.b} g, S {r.s} g, T {r.t} g</div>
                        <div className="text-[11px] text-slate-400 truncate">{(r.suroviny || []).join(", ")}</div>
                      </div>
                      <button type="button" onClick={() => pouzitRecept(basePath, ci, vi, r)}
                        className="btn btn-primary">
                        Použít
                      </button>
                    </div>
                  ))}
                </div>
              )}
              {/* Suroviny a Postup vedle sebe; výška roste s obsahem — bez vnitřního rolování */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-2">
                <label className="block">
                  <span className="text-[11px] font-semibold text-slate-400 uppercase tracking-wide">Suroviny</span>
                  <textarea value={(v.suroviny || []).join("\n")}
                    rows={Math.max(2, (v.suroviny || []).length + 1)}
                    placeholder={v.popis ? "Jedna na řádek, s gramáží, starší varianta má vše v Postupu" : "Jedna na řádek, s gramáží"}
                    onChange={(e) => setDraftField([...basePath, ci, "varianty", vi, "suroviny"], e.target.value.split("\n").filter((x) => x.trim()))}
                    className="block w-full text-xs leading-relaxed border border-slate-200 rounded-xl px-2.5 py-2 mt-0.5 resize-none overflow-hidden focus:border-plum-300 focus:outline-none" />
                </label>
                <label className="block">
                  <span className="text-[11px] font-semibold text-slate-400 uppercase tracking-wide">Postup</span>
                  <textarea value={v.postup ?? v.popis ?? ""}
                    rows={Math.max(2, Math.ceil(String(v.postup ?? v.popis ?? "").length / 60) + 1)}
                    placeholder="Postup přípravy"
                    onChange={(e) => setDraftField([...basePath, ci, "varianty", vi, v.postup == null && v.popis != null ? "popis" : "postup"], e.target.value)}
                    className="block w-full text-xs leading-relaxed border border-slate-200 rounded-xl px-2.5 py-2 mt-0.5 resize-none overflow-hidden focus:border-plum-300 focus:outline-none" />
                </label>
              </div>
            </div>
            );
          })}
        </div>
      </details>
    ))
  );

  const renderEditor = (jd) => (
    <div className="space-y-3">
      <div className="bg-gradient-to-br from-plum-50 via-white to-brand-50 border border-plum-100 rounded-xl p-4 space-y-2.5">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <span className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-plum-700"><Target className="h-3.5 w-3.5" /> Denní příjem a makra</span>
          {jd.vypocet && (
            <span className="text-[11px] text-slate-400" title={jd.vypocet.metodika}>
              BMR {jd.vypocet.bmr} × {jd.vypocet.koef}, TDEE {jd.vypocet.tdee}
            </span>
          )}
        </div>
        <div className="flex flex-wrap items-end gap-3">
          <label className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide">
            kcal/den
            <input type="number" value={jd.kalorie || ""} onChange={(e) => setKcalSMakry(parseInt(e.target.value, 10) || 0)}
              className="block w-28 text-lg font-bold text-slate-800 bg-white border-2 border-plum-200 rounded-xl px-2.5 py-1.5 mt-1 focus:border-plum-400 focus:outline-none" />
          </label>
          {[["bilkoviny", "Bílkoviny (g)"], ["sacharidy", "Sacharidy (g)"], ["tuky", "Tuky (g)"]].map(([m, l]) => (
            <label key={m} className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide">
              {l}
              <input type="number" value={jd.makra?.[m] ?? ""} onChange={(e) => setDraftField(["makra", m], parseInt(e.target.value, 10) || 0)}
                className="field block w-24 font-semibold mt-1" />
            </label>
          ))}
          {canManage && (
            <button type="button" onClick={prepocitatPorce}
              title="Přeškáluje gramáže (g/ml) a kcal/makra všech receptů na zadané kcal/den (poměr maker nemění). Kusová množství (ks, plátky) zkontrolujte ručně."
              className="inline-flex items-center gap-1.5 text-sm font-semibold text-white bg-gradient-to-r from-plum-600 to-brand-600 hover:from-plum-700 hover:to-brand-700 rounded-xl px-4 py-2 shadow-sm min-h-[44px] sm:min-h-0">
              <Scale className="h-4 w-4" /> Přepočítat recepty
            </button>
          )}
        </div>
        {canManage && (
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="text-[11px] font-semibold text-slate-400 uppercase tracking-wide mr-0.5">Poměr maker:</span>
            {MAKRA_PRESETY.map((p) => (
              <button key={p.label} type="button" onClick={() => aplikujPreset(p.pct)}
                title={`B ${p.pct[0]} %, S ${p.pct[1]} %, T ${p.pct[2]} %`}
                className={`text-[11px] font-medium rounded-full px-2.5 py-1 border transition-colors ${
                  presetAktivni(jd, p.pct)
                    ? "bg-plum-600 text-white border-plum-600"
                    : "bg-white text-plum-700 border-plum-200 hover:border-plum-400"
                }`}>
                {p.label} <span className="opacity-60">{p.pct.join("/")}</span>
              </button>
            ))}
            <button type="button" onClick={regenerovatSCilem} disabled={data?.generuje}
              title="AI vygeneruje nový jídelníček se zadanými kcal a poměrem maker, jen tak se nový poměr promítne do skladby jídel"
              className="text-[11px] font-semibold text-brand-700 bg-brand-50 hover:bg-brand-100 border border-brand-200 rounded-full px-2.5 py-1 disabled:opacity-50 min-h-[44px] sm:min-h-0">
              <Sparkles className="h-4 w-4" /> Vygenerovat s těmito makry
            </button>
          </div>
        )}
        {canManage && (
          <div className="text-[11px] text-plum-400">
            Změna kcal dopočítá gramy maker (drží aktuální poměr). Tlačítko Přepočítat recepty přeškáluje porce receptů na kcal, poměr maker ale nemění; pro jiný poměr (preset) použijte Vygenerovat s těmito makry (AI, ~2 min, nová varianta).
          </div>
        )}
      </div>

      {/* Hromadná záměna suroviny — ušetří přegenerování celého jídelníčku.
          Nejdřív se zásah spočítá lokálně z draftu, teprve po potvrzení jde AI. */}
      {canManage && (
        <div className="bg-white border border-plum-200 rounded-xl p-3.5 space-y-2.5">
          <div className="flex items-baseline gap-2 flex-wrap">
            <span className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-plum-700"><Repeat className="h-3.5 w-3.5" /> Zaměnit surovinu</span>
            <span className="text-[11px] text-slate-400">Vymění jednu surovinu ve všech jídlech naráz, bez generování celého jídelníčku.</span>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <input type="text" value={surZ}
              onChange={(e) => { setSurZ(e.target.value); setSurNalez(null); setSurZprava(""); }}
              placeholder="Co vyměnit, např. rostlinný protein"
              className="field flex-1 min-w-[180px]" />
            <span aria-hidden className="text-sm font-bold text-plum-300">→</span>
            <input type="text" value={surNa}
              onChange={(e) => { setSurNa(e.target.value); setSurNalez(null); setSurZprava(""); }}
              placeholder="Čím nahradit, např. syrovátkový protein"
              className="field flex-1 min-w-[180px]" />
            <button type="button" onClick={najdiSurovinu} disabled={surBusy}
              title="Spočítá, kolika jídel se záměna týká. AI se volá až po potvrzení."
              className="btn btn-primary whitespace-nowrap">
              <RefreshCw className="w-3.5 h-3.5" /> Najít a zaměnit
            </button>
          </div>
          {surZprava && <div className="text-[11px] font-semibold text-plum-700">{surZprava}</div>}
          {surNalez && (
            <div className="rounded-xl border border-plum-200 bg-plum-50/70 p-2.5 space-y-2">
              <div className="text-xs text-slate-700">
                Zaměníme <strong>{surNalez.z}</strong> → <strong>{surNalez.na}</strong> v {surNalez.jidla.length} {surNalez.jidla.length === 1 ? "jídle" : "jídlech"}. AI dopočítá novou gramáž i makra, aby jídla zůstala na svých kcal (±10 %). Zbytek surovin zůstane beze změny.
              </div>
              <ul className="space-y-0.5 max-h-40 overflow-auto">
                {surNalez.jidla.map((x, xi) => (
                  <li key={xi} className="flex items-center gap-1.5 flex-wrap text-[11px] text-slate-500">
                    <span className="font-medium text-slate-700">{x.nazev || "(bez názvu)"}</span>
                    {x.den ? (<><span aria-hidden className="w-px h-3 bg-current opacity-40" />{x.den}</>) : null}
                    {x.chod ? (<><span aria-hidden className="w-px h-3 bg-current opacity-40" />{x.chod}</>) : null}
                    <span aria-hidden className="w-px h-3 bg-current opacity-40" />
                    {x.kcal || "?"} kcal
                  </li>
                ))}
              </ul>
              <div className="flex items-center gap-2 flex-wrap">
                <button type="button" onClick={spustZamenuSuroviny} disabled={surBusy}
                  className="inline-flex items-center gap-1.5 text-xs font-semibold text-white bg-gradient-to-r from-brand-600 to-plum-600 hover:from-brand-700 hover:to-plum-700 rounded-xl px-3 py-2 shadow-sm disabled:opacity-60 min-h-[44px] sm:min-h-0">
                  <Sparkles className="w-3.5 h-3.5" />
                  {surBusy ? "AI přepočítává jídla…" : `Zaměnit v ${surNalez.jidla.length} ${surNalez.jidla.length === 1 ? "jídle" : "jídlech"}`}
                </button>
                <button type="button" onClick={() => { setSurNalez(null); setSurZprava(""); }} disabled={surBusy}
                  className="text-xs text-slate-500 hover:text-slate-700 border border-slate-300 rounded-xl px-3 py-2 disabled:opacity-60">
                  Zrušit
                </button>
                <span className="text-[11px] text-slate-400">Změna jde jen do konceptu, klientce se zobrazí až po „Uložit úpravy" (vrátíte ji tlačítkem ↶ Zpět).</span>
              </div>
            </div>
          )}
        </div>
      )}

      {canManage && (() => {
        const chybi = chybejiciVarianty(jd);
        const pocet = chybi.reduce((a, m) => a + m.chybi, 0);
        if (!pocet && !doplnZprava) return null;
        return (
          <div className="bg-white border border-amber-200 rounded-xl p-3.5 flex flex-wrap items-center gap-2">
            {pocet > 0 ? (
              <span className="text-xs text-slate-700 flex-1 min-w-[200px]">
                U {chybi.length} {chybi.length === 1 ? "chodu" : "chodů"} chybí varianty do pěti (celkem {pocet}). Hotové varianty zůstanou, doplní se jen chybějící.
              </span>
            ) : (
              <span className="text-xs font-semibold text-plum-700 flex-1 min-w-[200px]">{doplnZprava}</span>
            )}
            {pocet > 0 && (
              <button type="button" onClick={doplnVarianty} disabled={doplnBusy} className="btn btn-primary whitespace-nowrap">
                <Sparkles className="w-3.5 h-3.5" /> {doplnBusy ? "Doplňuji…" : "Doplnit chybějící varianty"}
              </button>
            )}
            {pocet > 0 && doplnZprava && <span className="text-[11px] font-semibold text-plum-700 w-full">{doplnZprava}</span>}
          </div>
        );
      })()}

      <label className="block text-xs text-slate-500">
        Úvod pro klientku
        <RostouciPole value={jd.uvod || ""} minRows={6} onChange={(e) => setDraftField(["uvod"], e.target.value)}
          className="field block w-full mt-0.5 leading-relaxed" />
      </label>
      {Array.isArray(jd.dny) && jd.dny.length > 0 ? (
        // Týdenní tvar: Po-Pá, každý den vlastní chody
        jd.dny.map((den, di) => (
          <details key={di} className="border-2 border-plum-100 rounded-xl" open={di === 0}>
            <summary className="px-3 py-2 text-sm font-bold text-plum-700 cursor-pointer bg-plum-50/60 rounded-t-xl">
              <CalendarDays className="mr-1.5 inline-block h-4 w-4 align-[-3px]" />{den.den || `Den ${di + 1}`}
            </summary>
            <div className="p-2 space-y-2">{renderChody(den.chody, ["dny", di, "chody"])}</div>
          </details>
        ))
      ) : (
        // Starší jednodenní tvar (zpětná kompatibilita)
        renderChody(jd.chody, ["chody"])
      )}
      <label className="block text-xs text-slate-500">
        Tipy (jeden na řádek)
        <TipyPole tipy={jd.tipy} onZmena={(pole) => setDraftField(["tipy"], pole)} />
      </label>
    </div>
  );

  return (
    <div id="jid-sekce" className="space-y-4" style={{ scrollMarginTop: 90 }}>
      {err ? <div className="text-sm text-rose-600 bg-rose-50 border border-rose-200 rounded-xl px-3 py-2">{err}</div> : null}

      {/* Upozornění na chybějící předpoklady */}
      {canManage && data && !data.maApiKlic && (
        <div className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2">
          <AlertTriangle className="mr-1.5 inline-block h-4 w-4 align-[-3px]" />Chybí <code className="font-mono text-xs">ANTHROPIC_API_KEY</code> v Netlify env (Site settings → Environment variables), bez něj nejde generovat.
        </div>
      )}
      {canManage && data && !data.maReference && (
        <div className="text-sm text-sky-700 bg-sky-50 border border-sky-200 rounded-xl px-3 py-2">
          <Lightbulb className="mr-1.5 inline-block h-4 w-4 align-[-3px]" />Znalostní báze (referenční jídelníčky FitWay40) je zatím prázdná, AI pojede podle obecné české fitness stravy. Nasdílejte jídelníčky a nahrajeme je.
        </div>
      )}

      {/* Žádosti klientky o úpravu jídelníčku (z portálu) */}
      {(data?.zadosti || []).length > 0 && (
        <div id="jid-zadosti" className="space-y-1.5" style={{ scrollMarginTop: 90 }}>
          <div className="inline-flex items-center gap-1.5 text-sm font-semibold text-amber-700"><MessageSquare className="h-4 w-4" /> Žádosti o úpravu jídelníčku</div>
          {data.zadosti.map((z) => (
            <div key={z.id} className="flex items-start gap-2 text-sm text-amber-900 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2">
              <span className="flex-1 whitespace-pre-line">{z.text}</span>
              {canManage && (
                <button
                  onClick={async () => { if (await post({ action: "zadost-vyrizena", id: z.id, klient_id: id })) await load(); }}
                  className="flex-shrink-0 text-xs font-medium text-amber-700 hover:text-white hover:bg-amber-600 border border-amber-300 rounded-lg px-2 py-1 transition-colors"
                >
                  Vyřízeno
                </button>
              )}
            </div>
          ))}
          <div className="text-[11px] text-slate-400">Upravte návrh (nebo vygenerujte znovu) a Zveřejněte, pak klikněte Vyřízeno, klientce zmizí „tým na tom pracuje".</div>
        </div>
      )}

      {/* Vlastní recepty klientky (12.7.) — schvaluje nutritionist/CSM/owner.
          Schválený recept se přidá do báze a generátor ho nabízí všem. */}
      {Array.isArray(data?.receptyNavrhy) && data.receptyNavrhy.length > 0 && (
        <div id="jid-recepty" className="space-y-2" style={{ scrollMarginTop: 90 }}>
          <div className="text-sm font-semibold text-slate-700">Recepty od klientky</div>
          {data.receptyNavrhy.map((rn) => (
            <div key={rn.id} className={`border rounded-xl px-3 py-2 ${rn.stav === "ceka" ? "bg-brand-50 border-brand-200" : "bg-slate-50 border-slate-200"}`}>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm font-medium text-slate-800">{rn.nazev}</span>
                {rn.chod && <span className="text-[11px] text-slate-500 bg-white border border-slate-200 rounded-full px-2 py-0.5">{rn.chod}</span>}
                {rn.makra?.kcal ? (
                  <span className="text-[11px] font-medium text-plum-700 bg-plum-50 border border-plum-200 rounded-full px-2 py-0.5 whitespace-nowrap"
                    title="AI odhad na 1 porci, před schválením zkontrolujte">
                    {rn.makra.kcal} kcal, B {rn.makra.b}, S {rn.makra.s}, T {rn.makra.t} g
                  </span>
                ) : rn.stav === "ceka" ? (
                  <span className="text-[11px] text-amber-600 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5">bez maker</span>
                ) : null}
                <span className={`text-[11px] font-medium rounded-full px-2 py-0.5 ${
                  rn.stav === "ceka" ? "text-brand-700 bg-brand-100"
                  : rn.stav === "schvaleno" ? "text-emerald-700 bg-emerald-100"
                  : "text-slate-500 bg-slate-200"
                }`}>{rn.stav === "ceka" ? "čeká na schválení" : rn.stav === "schvaleno" ? `schváleno${rn.resil ? `, ${rn.resil}` : ""}` : "zamítnuto"}</span>
                {canManage && rn.stav === "ceka" && (
                  <span className="ml-auto flex gap-1.5 flex-wrap">
                    <button
                      onClick={async () => {
                        setMakraBusy(rn.id);
                        const j = await postData({ action: "recept-makra", id: rn.id, klient_id: id });
                        setMakraBusy(null);
                        if (j) await load();
                      }}
                      disabled={makraBusy === rn.id}
                      title="AI odhadne kcal a makra z ingrediencí, bez nich recept do báze nepatří"
                      className="text-xs font-medium text-plum-700 bg-plum-50 hover:bg-plum-100 border border-plum-200 rounded-lg px-2.5 py-1 disabled:opacity-50">
                      {makraBusy === rn.id ? <><Sparkles className="h-3.5 w-3.5" /> Počítám…</> : <><Calculator className="h-3.5 w-3.5" /> {rn.makra?.kcal ? "Přepočítat makra" : "Dopočítat makra"}</>}
                    </button>
                    <button
                      onClick={async () => { if (await post({ action: "recept-schvalit", id: rn.id, klient_id: id })) await load(); }}
                      className="btn btn-primary">
                      Schválit do báze
                    </button>
                    <button
                      onClick={async () => { if (await post({ action: "recept-zamitnout", id: rn.id, klient_id: id })) await load(); }}
                      className="text-xs font-medium text-slate-500 hover:text-rose-600 border border-slate-300 hover:border-rose-300 rounded-lg px-2.5 py-1">
                      Zamítnout
                    </button>
                  </span>
                )}
              </div>
              {(rn.ingredience || rn.postup) && (
                <div className="mt-1.5 text-xs text-slate-600 space-y-1">
                  {rn.ingredience && <div className="whitespace-pre-line"><span className="text-slate-400">Ingredience:</span> {rn.ingredience}</div>}
                  {rn.postup && <div className="whitespace-pre-line"><span className="text-slate-400">Postup:</span> {rn.postup}</div>}
                </div>
              )}
            </div>
          ))}
          <div className="text-[11px] text-slate-400">Schválením se recept přidá do báze receptů i s kcal a makry (bez nich do báze nepatří, pokud nejsou dopočtené, dopočítají se při schválení automaticky). Generátor ho pak nabízí všem klientkám, klientka stav vidí v portálu.</div>
        </div>
      )}

      {/* Dotazník klientky — dropdown s přehledným detailem (10.7. v4) */}
      <div className="border border-slate-200 rounded-xl overflow-hidden bg-white">
        <button
          type="button"
          onClick={() => dotaznik && setDotOpen((o) => !o)}
          className={`w-full flex items-center gap-2 px-3 py-2.5 text-left ${dotaznik ? "hover:bg-slate-50" : "cursor-default"}`}
        >
          <span className="text-sm font-semibold text-slate-700 flex-shrink-0">Výživový dotazník</span>
          {dotaznik ? (
            <>
              <span className="text-[11px] font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5 flex-shrink-0">
                vyplněn {new Date(dotaznik.updated_at).toLocaleDateString("cs-CZ")}
              </span>
              {dotaznik.data?.cil && (
                <span className="hidden sm:inline text-[11px] text-slate-500 bg-slate-100 rounded-full px-2 py-0.5 truncate">{dotaznik.data.cil}</span>
              )}
              {(() => {
                const n = ["alergie", "vyloucit"].reduce((a, k) => a + (Array.isArray(dotaznik.data?.[k]) ? dotaznik.data[k].length : 0), 0)
                  + (dotaznik.data?.alergie_jine ? 1 : 0) + (dotaznik.data?.vyloucit_jine ? 1 : 0);
                return n > 0 ? (
                  <span className="hidden sm:inline text-[11px] font-medium text-rose-600 bg-rose-50 border border-rose-200 rounded-full px-2 py-0.5 flex-shrink-0">
                    {n} omezení
                  </span>
                ) : null;
              })()}
              <ChevronDown className={`w-4 h-4 text-slate-400 ml-auto flex-shrink-0 transition-transform ${dotOpen ? "rotate-180" : ""}`} />
            </>
          ) : (
            <span className="text-xs text-slate-400 ml-auto">klientka zatím nevyplnila (v portálu, záložka Jídelníček)</span>
          )}
        </button>
        {dotaznik && dotOpen && (
          <div className="border-t border-slate-200 bg-slate-50 px-3 py-3 space-y-3">
            {DOTAZNIK_SKUPINY.map((sk) => {
              const rows = sk.klice
                .map((k) => {
                  const raw = dotaznik.data?.[k];
                  if (raw == null || raw === "" || (Array.isArray(raw) && raw.length === 0)) return null;
                  return { k, label: DOTAZNIK_LABELS[k], raw };
                })
                .filter(Boolean);
              if (rows.length === 0) return null;
              return (
                <div key={sk.titul}>
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-400 mb-1.5">{sk.titul}</div>
                  {sk.dlouhe ? (
                    <div className="space-y-2">
                      {rows.map((r) => (
                        <div key={r.k} className="bg-white border border-slate-200 rounded-xl px-3 py-2">
                          <div className="text-[11px] text-slate-400 mb-0.5">{r.label}</div>
                          <div className="text-sm text-slate-700 whitespace-pre-line">{Array.isArray(r.raw) ? r.raw.join(", ") : r.raw}</div>
                        </div>
                      ))}
                    </div>
                  ) : sk.omezeni ? (
                    <div className="space-y-1.5">
                      {rows.map((r) => (
                        <div key={r.k} className="flex flex-wrap items-baseline gap-1.5 text-sm">
                          <span className="text-slate-400 flex-shrink-0">{r.label}:</span>
                          {Array.isArray(r.raw) ? (
                            r.raw.map((x, i) => (
                              <span key={i} className="text-xs font-medium text-rose-700 bg-rose-50 border border-rose-200 rounded-full px-2 py-0.5">{x}</span>
                            ))
                          ) : (
                            <span className="text-slate-700 font-medium">{r.raw}</span>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 text-sm">
                      {rows.map((r) => (
                        <div key={r.k} className="flex gap-2">
                          <span className="text-slate-400 flex-shrink-0">{r.label}:</span>
                          <span className="text-slate-700 font-medium">{Array.isArray(r.raw) ? r.raw.join(", ") : r.raw}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
            {dotaznik.data?._upravil && !dotEdit && (
              <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-xl px-2.5 py-1.5">
                <Pencil className="mr-1.5 inline-block h-3.5 w-3.5 align-[-2px]" />Naposledy upravila <b>{dotaznik.data._upravil}</b>
                {dotaznik.data._upraveno_at ? ` (${new Date(dotaznik.data._upraveno_at).toLocaleDateString("cs-CZ")} ${new Date(dotaznik.data._upraveno_at).toLocaleTimeString("cs-CZ", { hour: "2-digit", minute: "2-digit" })})` : ""}
                {" "}(původní verze od klientky je v historii níže). je v historii níže.
              </div>
            )}
            {canManage && !dotEdit && (
              <div className="pt-1 flex items-center gap-2 flex-wrap">
                <button onClick={zacniUpravuDotazniku}
                  className="text-xs font-medium text-slate-600 hover:text-slate-900 border border-slate-300 rounded-lg px-2.5 py-1 min-h-[44px] sm:min-h-0">
                  <Pencil className="h-4 w-4" /> Upravit dotazník
                </button>
                <span className="text-[11px] text-slate-400">Když klientka něco napíše mimo dotazník, můžete to sem doplnit. Každá změna se ukládá do historie.</span>
              </div>
            )}
            {dotEdit && (
              <div className="bg-white border border-amber-300 rounded-xl px-3 py-3 space-y-3">
                <div className="inline-flex items-center gap-1.5 text-xs font-bold text-amber-800"><Pencil className="h-3.5 w-3.5" /> Úprava dotazníku (uloží se pod vaším jménem, stará verze zůstane v historii)</div>
                {DOTAZNIK_SKUPINY.map((sk) => (
                  <div key={sk.titul}>
                    <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-400 mb-1.5">{sk.titul}</div>
                    <div className={sk.dlouhe ? "space-y-2" : "grid grid-cols-1 sm:grid-cols-2 gap-2"}>
                      {sk.klice.map((k) => (
                        <div key={k}>
                          <div className="text-[11px] text-slate-400 mb-0.5">
                            {DOTAZNIK_LABELS[k]}{DOT_POLE.includes(k) ? " (více hodnot oddělte čárkou)" : ""}
                          </div>
                          {sk.dlouhe ? (
                            <textarea value={dotAns[k] || ""} rows={2}
                              onChange={(e) => setDotAns((p) => ({ ...p, [k]: e.target.value }))}
                              className="field w-full" />
                          ) : (
                            <input value={dotAns[k] || ""}
                              onChange={(e) => setDotAns((p) => ({ ...p, [k]: e.target.value }))}
                              className="field w-full" />
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
                <div className="flex items-center gap-2">
                  <button onClick={ulozUpravuDotazniku} disabled={saving}
                    className="btn btn-primary">
                    {saving ? "Ukládám…" : "Uložit úpravy dotazníku"}
                  </button>
                  <button onClick={() => setDotEdit(false)}
                    className="text-xs text-slate-500 hover:text-slate-700 border border-slate-200 rounded-lg px-2.5 py-1.5">
                    Zrušit
                  </button>
                </div>
              </div>
            )}
            {(data?.dotaznikHistorie || []).length > 0 && (
              <div className="border-t border-slate-200 pt-2">
                <div className="mb-1.5 inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-slate-400"><History className="h-3.5 w-3.5" /> Historie verzí ({data.dotaznikHistorie.length})</div>
                <div className="space-y-1">
                  {data.dotaznikHistorie.map((hv) => (
                    <div key={hv.id} className="bg-white border border-slate-200 rounded-xl px-2.5 py-1.5">
                      <button onClick={() => setDotHistShow(dotHistShow === hv.id ? null : hv.id)}
                        className="w-full flex items-center gap-2 text-left">
                        <span className="text-xs text-slate-600">
                          <b>{hv.autor || "klientka"}</b>, {new Date(hv.created_at).toLocaleDateString("cs-CZ")} {new Date(hv.created_at).toLocaleTimeString("cs-CZ", { hour: "2-digit", minute: "2-digit" })}
                        </span>
                        <span className="ml-auto text-[11px] text-slate-400">{dotHistShow === hv.id ? "skrýt ▴" : "zobrazit ▾"}</span>
                      </button>
                      {dotHistShow === hv.id && (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-0.5 text-xs mt-1.5 pt-1.5 border-t border-slate-100">
                          {Object.keys(DOTAZNIK_LABELS).map((k) => {
                            const v = hv.data?.[k];
                            if (v == null || v === "" || (Array.isArray(v) && !v.length)) return null;
                            return (
                              <div key={k} className="flex gap-1.5">
                                <span className="text-slate-400 flex-shrink-0">{DOTAZNIK_LABELS[k]}:</span>
                                <span className="text-slate-700">{Array.isArray(v) ? v.join(", ") : String(v)}</span>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
            {isOwner && (
              <div className="pt-1">
                <button onClick={deleteDotaznik} title="Smazat vyplněný dotazník (jen owner)"
                  className="text-[11px] text-slate-400 hover:text-rose-600 border border-slate-200 hover:border-rose-300 rounded-lg px-2 py-0.5 min-h-[44px] sm:min-h-0">
                  Smazat dotazník
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Generování */}
      {canManage && dotaznik && (
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={generate}
            disabled={data?.generuje}
            className="btn btn-primary"
          >
            <Sparkles className="w-4 h-4" />
            {data?.generuje ? "Generuje se…" : navrh || zverejneny ? "Vygenerovat znovu (AI)" : "Vygenerovat návrh (AI)"}
          </button>
          {data?.generuje && (
            <div className="w-full sm:max-w-md">
              {/* Prvni faze (vyber receptu Sonnetem) je jedno ~2min volani bez
                  ticků — do prvniho ticku se % odhaduje z casu, pak prebiraji
                  serverova (13.8.2026, Láďa: "2 minuty ukazuje 0 %"). */}
              <AIGenerating pct={Math.max(
                data?.generujeInfo && data.generujeInfo.celkem
                  ? (100 * (data.generujeInfo.hotovo || 0)) / data.generujeInfo.celkem
                  : 0,
                Math.min(60, odhadPct(genStartRef.current, 240)),
              )} />
            </div>
          )}
          {data?.chyba && !data?.generuje && (
            <span className="text-xs text-rose-600">Poslední generování selhalo: {data.chyba}</span>
          )}
        </div>
      )}

      {/* Schválení příjmu před generováním (15.7.2026) */}
      {canManage && genPanel && !data?.generuje && (() => {
        const kc = parseInt(genPanel.pr.kalorie, 10) || 0;
        const pct = (g, nasob) => (kc ? Math.round(((parseInt(g, 10) || 0) * nasob / kc) * 100) : 0);
        return (
        <div className="bg-emerald-50/70 border border-emerald-200 rounded-xl p-3 space-y-2">
          <div className="inline-flex items-center gap-1.5 text-xs font-bold text-emerald-800"><Scale className="h-3.5 w-3.5" /> Návrh denního příjmu ke schválení</div>
          {/* Klubová úroveň nemá z čeho počítat BMR, kalorie jsou zadané
              úrovní. Text o Mifflin-St Jeor by u ní byl matoucí. */}
          {genPanel.vypocet?.klub ? (
            <div className="text-[11px] text-slate-600 leading-relaxed">
              Klubová úroveň: kalorie jsou dané úrovní (<b>{genPanel.vypocet.kalorie} kcal</b>), nepočítají se
              z váhy a výšky. Rozložení klubu: bílkoviny 1,4 g na kilogram, tuky 30 % energie, zbytek sacharidy.
              Hodnoty můžete upravit, generuje se až po potvrzení.
            </div>
          ) : genPanel.vypocet ? (
            <div className="text-[11px] text-slate-600 leading-relaxed">
              Vypočteno metodikou FitWay40: bazální metabolismus (Mifflin-St Jeor) <b>{genPanel.vypocet.bmr} kcal</b> ×
              koeficient aktivity <b>{String(genPanel.vypocet.koef).replace(".", ",")}</b> = celkový výdej <b>{genPanel.vypocet.tdee} kcal</b>,
              cíl {cilFaktorText(data?.dotaznik?.data?.cil)}. Rozložení výchozí 25 % bílkoviny / 45 % sacharidy / 30 % tuky.
              Hodnoty můžete upravit, generuje se až po potvrzení.
            </div>
          ) : (
            <div className="text-[11px] text-amber-700">
              V dotazníku chybí věk, výška nebo váha, příjem nejde spočítat. Zadejte ho ručně, jinak kalorie odhadne AI jen zhruba.
            </div>
          )}
          <div className="flex flex-wrap items-end gap-2.5">
            <div>
              <div className="text-[11px] font-semibold text-slate-500 mb-0.5">Kalorie / den</div>
              <input type="number" value={genPanel.pr.kalorie}
                onChange={(e) => setGenPanel((p) => ({ ...p, pr: { ...p.pr, kalorie: e.target.value } }))}
                onBlur={genZmenKcalBlur}
                className="field w-24 font-bold" />
            </div>
            {[["bilkoviny", "Bílkoviny (g)", 4], ["sacharidy", "Sacharidy (g)", 4], ["tuky", "Tuky (g)", 9]].map(([k, lbl, nasob]) => (
              <div key={k}>
                <div className="text-[11px] font-semibold text-slate-500 mb-0.5">{lbl}, {pct(genPanel.pr[k], nasob)} %</div>
                <input type="number" value={genPanel.pr[k]}
                  onChange={(e) => genZmenMakro(k, e.target.value)}
                  className="field w-20" />
              </div>
            ))}
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <button onClick={spustGenerovani}
              className="btn btn-primary">
              <Sparkles className="w-4 h-4" /> Sedí to, vygenerovat
            </button>
            <button onClick={() => setGenPanel(null)}
              className="text-xs text-slate-500 hover:text-slate-700 border border-slate-300 rounded-lg px-3 py-2">
              Zrušit
            </button>
          </div>
        </div>
        );
      })()}

      {/* Varianty jídelníčku — aktuální samostatně, starší návrhy a archiv ve sbaleném dropdownu */}
      {(data?.varianty || []).length > 0 && (() => {
        // „Aktuální" = zveřejněná varianta; když žádná není, tak naplánovaná.
        // Zbytek (návrhy + archiv) jde do dropdownu, defaultně zavřeného.
        const vsechnyVarianty = data.varianty;
        const aktualniVarianta = vsechnyVarianty.find((v) => v.stav === "zverejneno")
          || vsechnyVarianty.find((v) => v.stav === "naplanovano")
          // Nic zveřejněného ani naplánovaného → nahoru patří nejnovější návrh
          // (typicky ten, co AI právě dogenerovala). Jinak by po vygenerování
          // nebylo nahoře nic a člověk by musel rozklikávat sbalený dropdown.
          || vsechnyVarianty.find((v) => v.stav === "navrh")
          || null;
        const starsiVarianty = vsechnyVarianty.filter((v) => v !== aktualniVarianta);
        const renderVarianta = (v) => {
          const s = variantSummary(v);
          const isPub = v.stav === "zverejneno";
          const isArch = v.stav === "archiv";
          const isPlan = v.stav === "naplanovano";
          const isNavrh = v.stav === "navrh";
          return (
            <div key={v.id} className={`flex flex-wrap items-center gap-x-3 gap-y-1.5 text-sm border rounded-xl px-3 py-2 ${
              isPub ? "bg-emerald-50/70 border-emerald-200" : isArch ? "bg-slate-50 border-slate-200" : "bg-amber-50/50 border-amber-200"
            }`}>
              <span className={`text-[11px] font-bold uppercase tracking-wide rounded px-1.5 py-0.5 border ${
                isPub ? "text-emerald-700 bg-emerald-100 border-emerald-200" : isArch ? "text-slate-500 bg-slate-100 border-slate-200" : "text-amber-700 bg-amber-100 border-amber-200"
              }`}>
                {isPub ? "v portálu" : isArch ? "archiv" : isPlan ? "naplánováno" : "návrh"}
              </span>
              {isPlan && (
                <span className="text-[11px] font-medium text-sky-700 bg-sky-50 border border-sky-200 rounded-full px-2 py-0.5 whitespace-nowrap">
                  ⏰ zveřejní se {fmtKdy(v.zverejnit_at)}
                </span>
              )}
              {koncepty[v.id] && (
                <span title="U téhle varianty máš rozdělané neuložené změny"
                  className="inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wide rounded px-1.5 py-0.5 border text-amber-800 bg-amber-100 border-amber-300">
                  <span aria-hidden className="w-1.5 h-1.5 rounded-full bg-amber-500" /> koncept
                </span>
              )}
              <span className="text-xs text-slate-400 whitespace-nowrap inline-flex items-center gap-1.5">
                {new Date(v.created_at).toLocaleDateString("cs-CZ")}
                {v.generated_by ? (<><span aria-hidden className="w-px h-3 bg-current opacity-40" />{v.generated_by}</>) : null}
              </span>
              <span className="font-semibold text-slate-800 whitespace-nowrap">{s.kcal} kcal</span>
              <span className="text-slate-600 whitespace-nowrap inline-flex items-center gap-1.5" title="Bílkoviny / Sacharidy / Tuky">
                B {s.b} g <span aria-hidden className="w-px h-3 bg-current opacity-40" /> S {s.s} g <span aria-hidden className="w-px h-3 bg-current opacity-40" /> T {s.t} g
              </span>
              <span className="text-slate-500 whitespace-nowrap">{s.chodyCount}</span>
              {canManage && (
                <span className="ml-auto flex items-center gap-1.5">
                  <button onClick={() => openEditor(v)}
                    className={`text-xs font-medium border rounded-lg px-2 py-1 ${editId === v.id ? "text-white bg-slate-700 border-slate-700" : "text-slate-600 hover:bg-slate-100 border-slate-300"}`}>
                    {editId === v.id ? "V editoru" : koncepty[v.id] ? "Pokračovat v konceptu" : "Upravit"}
                  </button>
                  {/* Korektura zapisuje ROVNOU do DB (na rozdíl od editoru) — píšeme to i do titulku. */}
                  <button onClick={() => spustKorekturu(v)} disabled={korBusy === v.id}
                    title="Pustí českého korektora nad uloženou variantou (slovenské výrazy, chybějící diakritika, překlepy, sloh) a opravy ROVNOU uloží do databáze, nic se nepotvrzuje v editoru."
                    className="text-xs font-medium text-plum-700 border border-plum-300 bg-plum-50 hover:bg-plum-100 rounded-lg px-2 py-1 disabled:opacity-60 disabled:cursor-wait">
                    {korBusy === v.id ? "Kontroluji…" : <><SpellCheck className="h-3.5 w-3.5" /> Projet korektorem</>}
                  </button>
                  {!isPub && !isPlan && (
                    <button onClick={() => publish(v)}
                      className="btn btn-primary">
                      Zveřejnit
                    </button>
                  )}
                  {!isPub && !isArch && !isPlan && (
                    <button onClick={() => { setPlanId(planId === v.id ? null : v.id); if (!planKdy) setPlanKdy(presetKdy(2, 8)); }}
                      className={`text-xs font-medium border rounded-lg px-2 py-1 ${planId === v.id ? "text-sky-800 bg-sky-100 border-sky-300" : "text-sky-700 bg-sky-50 hover:bg-sky-100 border-sky-200"}`}>
                      ⏰ Později
                    </button>
                  )}
                  {isPlan && (
                    <button onClick={() => zrusitPlanZverejneni(v)}
                      className="text-xs font-medium text-sky-700 hover:text-sky-900 border border-sky-300 bg-white rounded-lg px-2 py-1">
                      Zrušit plán
                    </button>
                  )}
                  {isPub && (
                    <button onClick={unpublish}
                      className="text-xs font-medium text-slate-500 hover:text-rose-600 border border-slate-300 hover:border-rose-300 rounded-lg px-2 py-1 min-h-[44px] sm:min-h-0">
                      Stáhnout z portálu
                    </button>
                  )}
                  {!isPub && !isArch && (
                    <button onClick={() => archiveVariant(v)} title="Archivovat variantu (zůstane v seznamu)"
                      className="text-xs text-slate-400 hover:text-slate-700 border border-slate-200 hover:border-slate-400 rounded-lg px-2 py-1">
                      Archivovat
                    </button>
                  )}
                  {/* Návrh smí zahodit každý, kdo umí spravovat (i výživová poradkyně),
                      ostatní nezveřejněné stavy maže dál jen owner. */}
                  {!isPub && (isNavrh ? canManage : isOwner) && (
                    <button onClick={() => deleteVariant(v)} title={isNavrh ? "Zahodit návrh" : "Smazat variantu natrvalo (jen owner)"}
                      className="text-xs text-slate-400 hover:text-rose-600 border border-slate-200 hover:border-rose-300 rounded-lg px-2 py-1">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                </span>
              )}
              {korVysledek && korVysledek.id === v.id && (
                <div className="w-full flex items-start gap-1.5 text-[11px] text-plum-800 bg-plum-50 border border-plum-200 rounded-lg px-2 py-1 mt-0.5">
                  <SpellCheck aria-hidden className="h-3.5 w-3.5" />
                  <span>{korVysledek.text}</span>
                  <button onClick={() => setKorVysledek(null)} title="Skrýt"
                    className="ml-auto text-plum-400 hover:text-plum-700 leading-none">×</button>
                </div>
              )}
              {planId === v.id && !isPlan && (
                <div className="w-full flex items-center gap-1.5 flex-wrap pt-1.5 mt-0.5 border-t border-sky-100">
                  <span className="text-[11px] font-bold text-sky-800 uppercase tracking-wide">⏰ Zveřejnit</span>
                  <button onClick={() => setPlanKdy(presetKdy(1, 8))}
                    className={`text-[11px] font-medium rounded-full px-2.5 py-1 border ${planKdy === presetKdy(1, 8) ? "bg-sky-600 text-white border-sky-600" : "bg-white text-sky-700 border-sky-300 hover:bg-sky-100"}`}>
                    Zítra 8:00
                  </button>
                  <button onClick={() => setPlanKdy(presetKdy(2, 8))}
                    className={`text-[11px] font-medium rounded-full px-2.5 py-1 border ${planKdy === presetKdy(2, 8) ? "bg-sky-600 text-white border-sky-600" : "bg-white text-sky-700 border-sky-300 hover:bg-sky-100"}`}>
                    Za 2 dny 8:00
                  </button>
                  <span className="text-[11px] text-sky-600">nebo:</span>
                  <input type="datetime-local" value={planKdy} onChange={(e) => setPlanKdy(e.target.value)}
                    className="field w-auto" />
                  <button onClick={() => zverejnitPozdeji(v)} disabled={!planKdy}
                    className="btn btn-primary">
                    Naplánovat
                  </button>
                  <span className="text-[11px] text-sky-600">Klientce se zobrazí sám v naplánovaný čas (přesnost do 10 minut).</span>
                </div>
              )}
            </div>
          );
        };
        return (
          <div>
            <div className="text-sm font-semibold text-slate-700 mb-2">Varianty jídelníčku</div>
            {aktualniVarianta && (
              <div className="space-y-1.5 mb-2">{renderVarianta(aktualniVarianta)}</div>
            )}
            {starsiVarianty.length > 0 && (
              <div className="border border-slate-200 rounded-xl bg-white overflow-hidden">
                <button type="button" onClick={() => setStarsiOpen((o) => !o)}
                  className="w-full flex items-center gap-2 px-3 py-2.5 text-left hover:bg-slate-50">
                  <span className="text-sm font-semibold text-slate-700">Starší návrhy a archiv ({starsiVarianty.length})</span>
                  {starsiOpen
                    ? <ChevronUp className="w-4 h-4 text-slate-400 ml-auto flex-shrink-0" />
                    : <ChevronDown className="w-4 h-4 text-slate-400 ml-auto flex-shrink-0" />}
                </button>
                {starsiOpen && (
                  <div className="border-t border-slate-200 bg-slate-50 px-3 py-3 space-y-1.5">
                    {starsiVarianty.map((v) => renderVarianta(v))}
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })()}

      {/* Editor otevřené varianty */}
      {editId && draft && (
        <div>
          <div className="flex items-center gap-2 mb-2 flex-wrap">
            <span className="text-sm font-semibold text-slate-700">Úprava varianty</span>
            {dirty && (
              <span className="inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wide rounded px-1.5 py-0.5 border text-amber-800 bg-amber-100 border-amber-300">
                <span aria-hidden className="w-1.5 h-1.5 rounded-full bg-amber-500" /> koncept
              </span>
            )}
            {canManage && (
              <span className="ml-auto flex items-center gap-2">
                <button onClick={zpetDraft} disabled={!undoLen} title="Vrátit poslední změnu"
                  className="btn btn-secondary">
                  <Undo2 className="w-4 h-4" strokeWidth={2.5} /> Zpět{undoLen ? ` (${undoLen})` : ""}
                </button>
                <button onClick={saveDraft} disabled={saving}
                  className={`inline-flex items-center gap-1 text-xs font-medium text-white rounded-lg px-2.5 py-1.5 disabled:opacity-50 ${saved ? "bg-emerald-600" : "bg-slate-700 hover:bg-slate-800"}`}>
                  {saving ? "Ukládám…" : saved ? "Uloženo" : "Uložit úpravy"}
                </button>
                <button onClick={() => publish(data.varianty.find((v) => v.id === editId))}
                  className="btn btn-primary">
                  <Eye className="w-3.5 h-3.5" /> Zveřejnit klientce
                </button>
                <button onClick={zavritEditor}
                  className="text-xs text-slate-500 hover:text-slate-700 border border-slate-200 rounded-lg px-2 py-1.5 min-h-[44px] sm:min-h-0">
                  Zavřít editor
                </button>
              </span>
            )}
          </div>
          {/* Neuložený koncept – vidí se na první pohled a nabízí Zahodit / Uložit. */}
          {dirty && (
            <div className="sticky top-0 z-20 mb-2 flex flex-wrap items-center gap-2 rounded-xl border border-amber-300 bg-amber-50 px-3 py-2 shadow-sm">
              <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-amber-900">
                <span aria-hidden className="w-2 h-2 rounded-full bg-amber-500" />
                Neuložený koncept
              </span>
              <span aria-hidden className="w-px h-3 bg-amber-500 opacity-40" />
              <span className="text-xs text-amber-800">
                {konceptObnoven
                  ? "Obnovili jsme tvoje rozdělané změny z minule. Klientka je zatím nevidí."
                  : "Vidíš rozdělané změny, ne uloženou verzi. Klientce se zobrazí až po uložení."}
              </span>
              {canManage && (
                <span className="ml-auto flex items-center gap-1.5">
                  <button onClick={zahodKoncept}
                    className="text-xs font-semibold text-amber-900 bg-white border border-amber-300 hover:border-amber-500 rounded-lg px-2.5 py-1.5 min-h-[44px] sm:min-h-0">
                    Zahodit
                  </button>
                  <button onClick={saveDraft} disabled={saving}
                    className="btn btn-primary">
                    {saving ? "Ukládám…" : "Uložit"}
                  </button>
                </span>
              )}
            </div>
          )}
          {renderEditor(draft)}
        </div>
      )}

      <ConfirmDialog
        open={!!confirmState}
        title={confirmState?.title || ""}
        description={confirmState?.description}
        confirmLabel={confirmState?.confirmLabel || "Potvrdit"}
        cancelLabel={confirmState?.cancelLabel || "Zrušit"}
        variant={confirmState?.variant || "danger"}
        onConfirm={() => { const fn = confirmState?.onConfirm; setConfirmState(null); if (fn) fn(); }}
        onCancel={() => setConfirmState(null)}
      />
    </div>
  );
}

/**
 * Pole Tipy (feedback #120, 21.9.2026). Dřív se prázdné řádky mazaly už při
 * psaní, takže Enter na konci nic neudělal a nový tip nešel začít. Teď si pole
 * drží vlastní text i s prázdnými řádky a do jídelníčku posílá jen neprázdné.
 * Roste s textem jako Úvod.
 */
function TipyPole({ tipy, onZmena }) {
  const zPole = (p) => (Array.isArray(p) ? p : []).join("\n");
  const [text, setText] = useState(() => zPole(tipy));
  // Když se tipy změní zvenku (nový návrh, vrácení změn), převezme je.
  useEffect(() => {
    const cisty = text.split("\n").filter((x) => x.trim()).join("\n");
    if (cisty !== zPole(tipy)) setText(zPole(tipy));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tipy]);
  return (
    <RostouciPole value={text} minRows={4}
      onChange={(e) => {
        setText(e.target.value);
        onZmena(e.target.value.split("\n").filter((x) => x.trim()));
      }}
      className="field block w-full mt-0.5" />
  );
}
